/**
 * `ChildrenListResponse` -> what the grid renders (STD-029 B-4).
 *
 * The whole of what the browser now does to a listing. Everything that used to
 * happen here — synthesising folders from key segments, deriving a folder's file
 * count and dominant serial, hiding empty folders under a filter, sorting — is
 * server-side, computed over the folder's whole subtree from one tree read. What
 * is left is a rename of fields.
 *
 * That is the point of the arc rather than a side effect: those rules decided
 * numbers a user reads and believes, they lived in a hook this app has no test
 * tier for (no jsdom, no RTL), and they were computed over whatever slice of the
 * tree the flat projection happened to send. Moving them did not simplify the
 * browser so much as make them *checkable*.
 *
 * Pure and exported so it can be unit-tested directly — for the same reason.
 */
import type { ChildEntry, ChildrenListResponse, UnregisteredEntry } from '../../types/api';
import type { AnalyticsHandoffFile } from './utils';
import type { FileItem } from './types';

/**
 * One registry-backed child.
 *
 * `key` carries the server's `path`, which is display data and nothing else
 * (R-1). `object_id` is always present on this arm — that is what makes the
 * entry shareable, renamable, movable and deletable.
 */
export function entryToFileItem(entry: ChildEntry): FileItem {
  return {
    name: entry.name,
    key: entry.path,
    size: entry.size,
    last_modified: entry.last_modified,
    url: '',
    object_id: entry.object_id,
    organization_id: entry.organization_id,
    isFolder: entry.is_folder,
    flight_id: entry.flight_id,
    serial_number: entry.serial_number,
    flight_date: entry.flight_date,
    flight_type: entry.flight_type,
    folderMetadata: entry.aggregates
      ? {
          fileCount: entry.aggregates.file_count,
          mostRecentDate: entry.aggregates.newest_flight_date,
          primarySerial: entry.aggregates.dominant_serial,
        }
      : undefined,
  };
}

/**
 * One R-9 entry: a flight whose bytes have no registry row.
 *
 * **It gets no `object_id`, and it cannot** — the source type has no such field,
 * so this is enforced by the compiler rather than by remembering. Every id-keyed
 * affordance therefore hides for it (share, rename, move, delete, verdict
 * badge), which is R-9's degradation: visible and inert, never offered-then-
 * refused.
 *
 * **`key` and `downloadPath` are filled from different fields, and that is the
 * fix for a real 404.** `key` is display — it falls back to `name` so a card
 * always has a label — while `downloadPath` is only ever the server's `path`,
 * which is `null` whenever the flight's `s3_key` is a bare uuid (every upload
 * since #39 Phase C) or sits outside the caller's own org prefix. The first cut
 * of B-4 gave the client only `name` and sent it as a path: a 404 for anything
 * not at the org root, and an unresolvable request once the flight-id fallback
 * had fired. R-9's rule is to degrade by HIDING, so `isDownloadableEntry` reads
 * `downloadPath` and the menu withholds the item.
 */
export function unregisteredToFileItem(entry: UnregisteredEntry): FileItem {
  return {
    name: entry.name,
    key: entry.path ?? entry.name,
    downloadPath: entry.path ?? undefined,
    size: entry.size,
    last_modified: entry.last_modified,
    url: '',
    isFolder: false,
    flight_id: entry.flight_id,
    serial_number: entry.serial_number,
    flight_date: entry.flight_date,
    flight_type: entry.flight_type,
  };
}

/**
 * Can this entry's BYTES be fetched at all?
 *
 * Two addresses lead to bytes and an entry has at most one of them: a registry
 * row is presigned by `object_id` (the only addressing that reaches inside a
 * shared folder), and an R-9 entry by the path the server derived — when it
 * could derive one.
 *
 * A folder is false for the reason `/download` answers `FILE_OBJECT_IS_FOLDER`:
 * it has no bytes (`blob_key IS NULL`), so offering the control and then
 * explaining the refusal is worse than not offering it.
 *
 * Gates Download **and** the analytics handoff, because both need to name the
 * object to something other than this browser.
 */
export function isDownloadableEntry(
  entry: Pick<FileItem, 'object_id' | 'downloadPath' | 'isFolder'>
): boolean {
  if (entry.isFolder) return false;
  return !!entry.object_id || !!entry.downloadPath;
}

/**
 * What Data Analysis Pro should be handed for one entry, or `null` to withhold
 * the action.
 *
 * Extracted out of `useFileOperations` because it is a rule about *which file
 * the other application will open*, and getting it wrong is silent — this app
 * ships no jsdom/RTL, so a rule left inside a hook has no tier that can hold it.
 *
 * **`s3_key` is sent only for the caller's OWN files.** `analytics-backend`'s
 * listing scopes paths to the caller's own `organization_id` on top of RLS, so a
 * share-root-relative path resolves in the WRONG namespace. The usual outcome is
 * a 404; the outcome that matters is the other one, where the caller happens to
 * own a file at the same relative path and it is silently plotted instead — on a
 * screen about somebody else's data. Sending the id alone makes that
 * unexpressible: an Analytics build that does not read `object_id` rejects the
 * entry and says so, which is the honest failure.
 *
 * `null` for a folder and for an R-9 entry with no resolvable path — the same
 * predicate Download uses, because both need an address the other side can
 * resolve.
 */
export function analyticsHandoffEntry(
  entry: Pick<
    FileItem,
    'object_id' | 'downloadPath' | 'isFolder' | 'key' | 'name' | 'organization_id'
  >,
  ownOrganizationId: string | null | undefined
): AnalyticsHandoffFile | null {
  if (!isDownloadableEntry(entry)) return null;
  const isForeign =
    !!entry.organization_id && !!ownOrganizationId && entry.organization_id !== ownOrganizationId;
  if (isForeign) {
    return { filename: entry.name, object_id: entry.object_id };
  }
  return { s3_key: entry.key, filename: entry.name, object_id: entry.object_id };
}

/**
 * The rendered list: registry children first, then any R-9 entries.
 *
 * The order is the server's — it sorted, because the page is paginated and
 * sorting a truncated page on the client is a lie about which rows you were
 * given. The unregistered arm is appended rather than merged into that sort:
 * it is a different kind of thing (a flight, not a row), it appears only at the
 * caller's own root, and in steady state it is empty. Sorting it in would make
 * a degraded state change the position of healthy rows.
 */
export function listItems(response: ChildrenListResponse | undefined): FileItem[] {
  if (!response) return [];
  return [
    ...response.entries.map(entryToFileItem),
    ...response.unregistered.map(unregisteredToFileItem),
  ];
}

/**
 * Is the open folder owned by ANOTHER organization?
 *
 * The one question two decisions turn on: whether to show the upload zone
 * (§3.6 — a share-relative path is not a valid `destination_path`, and the
 * id-addressed upload runs no flight-ingest pipeline) and whether to render the
 * breadcrumb as a share.
 *
 * Asked of the NODE's owner rather than of the ancestor chain, because that is
 * the fact itself rather than a proxy for it. `false` while nothing has loaded
 * and for an organization with no root — both mean "not demonstrably somebody
 * else's", and the failure direction matters: guessing *true* would hide the
 * upload zone in the caller's own tree.
 */
/**
 * How many entries the server actually returned for this page.
 *
 * The growth notice compares `total` against THIS, not against the rendered
 * list. `total` counts registry children matching the filters; the rendered list
 * additionally holds the R-9 `unregistered` arm, which the server counts in
 * neither. So `total > files.length` under-reports by however many unregistered
 * entries are on screen — and at the root, where that arm lives, it can hide the
 * notice entirely (5 of 6 shown + 1 unregistered = 6, and the user is told
 * nothing was truncated).
 */
export function pageEntryCount(response: ChildrenListResponse | undefined): number {
  return response?.entries.length ?? 0;
}

export function isForeignFolder(
  response: ChildrenListResponse | undefined,
  ownOrganizationId: string | null | undefined
): boolean {
  const owner = response?.node?.organization_id;
  if (!owner || !ownOrganizationId) return false;
  return owner !== ownOrganizationId;
}

/**
 * Which endpoint an upload from the open folder goes to (#1351).
 *
 * A `parent_id` means the id-addressed `POST /api/file-objects/{parent_id}/upload`;
 * `undefined` means the path-addressed `POST /api/files/upload` with the
 * folder's display path. The rule: inside a folder another organization
 * shared there IS no path — a path is a coordinate in the caller's own tree —
 * so the upload has to be addressed by id. In the caller's own tree the path
 * endpoint stays the route, because its root has no id (`parent_id: null` is
 * a body field on create-folder, not a path segment on upload).
 *
 * A foreign folder without an id cannot happen — the listing that made it
 * foreign carried the node's id — but if it ever did, answering `undefined`
 * would send the upload into the caller's own tree at a share-root-relative
 * path: the misfile the whole B-4 withholding existed to prevent. So that case
 * answers `null`, "unaddressable", and the caller refuses the upload outright
 * rather than letting it degrade into the wrong tree.
 */
export function uploadParentId(
  insideShare: boolean,
  currentFolder: { id: string | null }
): string | null | undefined {
  if (!insideShare) return undefined;
  return currentFolder.id ?? null;
}
